import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { fetch } from '@nx/angular';

import * as UsersActions from './users.actions';
import { Store } from '@ngrx/store';
import { map, tap, withLatestFrom } from 'rxjs/operators';
import { Router } from '@angular/router';
import { UsersService } from '@boma/supplier-portal/shared/services';

@Injectable()
export class UsersEffects {
  // Load users
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadUsers),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          return this.usersService
            .listUsers(
              user.selectedOrganizationId,
              action.query,
              action.page,
              action.perPage,
              action.archived
            )
            .pipe(
              map((response) => {
                return UsersActions.loadUsersSuccess({
                  payload: response,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.loadUsersFailure({ error });
        },
      })
    )
  );

  // Load user
  loadUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.getUserEntity),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          return this.usersService.getUser(action.entityId, action.userId).pipe(
            map((response) => {
              return UsersActions.getUserEntitySuccess({
                payload: response,
              });
            })
          );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.getUserEntityFailure({ error });
        },
      })
    )
  );

  // Find an existing user account from their phone number
  findUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.getUserAccount),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          return this.usersService
            .getUserByAccount(
              user.selectedOrganizationId,
              action.emailAddressOrPhoneNumber
            )
            .pipe(
              map((response) => {
                return UsersActions.getUserAccountSuccess({
                  payload: response,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.getUserAccountFailure({ error });
        },
      })
    )
  );

  // Create a new user
  createUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.createUserAccount),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.usersService
            .createUser(user.selectedOrganizationId, action.user)
            .pipe(
              map((response) =>
                UsersActions.createUserAccountSuccess({
                  payload: response,
                })
              ),
              tap(() => this.router.navigate(['/', 'users']))
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.createUserAccountFailure({
            error,
          });
        },
      })
    )
  );

  deleteUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.deleteUser),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.usersService
            .deleteUser(user.selectedOrganizationId, action.userId)
            .pipe(
              map((response) =>
                UsersActions.deleteUserSuccess({
                  userId: action.userId,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.deleteUserFailure({
            error: error,
            userId: action.userId,
          });
        },
      })
    )
  );

  restoreUserAccount$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.restoreArchivedUser),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.usersService
            .restoreUser(user.selectedOrganizationId, action.userId)
            .pipe(
              map((response) =>
                UsersActions.restoreArchivedUserSuccess({
                  payload: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.restoreArchivedUserFailure({
            error: error,
          });
        },
      })
    )
  );

  loadArchivedUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.loadArchivedUsers),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state; // 'organization'
          return this.usersService
            .listUsers(
              user.selectedOrganizationId,
              action.query,
              action.page,
              action.perPage,
              true
            )
            .pipe(
              map((response) => {
                return UsersActions.loadArchivedUsersSuccess({
                  payload: response,
                  filters: action,
                });
              })
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.loadArchivedUsersFailure({ error });
        },
      })
    )
  );

  // Update User Entity
  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UsersActions.updateUserEntity),
      fetch({
        run: (action, state: any) => {
          return this.usersService
            .updateUser(action.entityId, action.userId, action.data)
            .pipe(
              map((response) =>
                UsersActions.updateUserEntitySuccess({
                  payload: response,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return UsersActions.updateUserEntityFailure({
            error,
          });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private usersService: UsersService,
    private router: Router
  ) {}
}
